import { useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Divider from 'LuxKit/Divider'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useListTruncator from 'hooks/useListTruncator'
import useToggle from 'hooks/useToggle'
import ChatListSectionLabel from 'tripPlanner/pages/chat/ChatListSectionLabel'
import ChatListShowMoreToggle from 'tripPlanner/pages/chat/ChatListShowMoreToggle'
import ChatLandingUpcomingTripRow from './ChatLandingUpcomingTripRow'
import type { OrderTileData } from './bookingSelectionUtils'

interface Props {
  trips: Array<OrderTileData>
  disabled: boolean
  submittingOrderIds: ReadonlyArray<string>
  onPlanClick: (trip: OrderTileData) => void
}

function ChatLandingUpcomingTrips({ trips, disabled, submittingOrderIds, onPlanClick }: Props) {
  const intl = useIntl()
  const [showAll, toggleShowAll] = useToggle()
  const moreTripsId = useId()
  const [[firstTrip], moreTrips] = useListTruncator(1, trips)

  const showMoreLabel = intl.formatMessage({
    id: 'Qyy-Egdww_pCQIE7Huem',
    defaultMessage: '{count, plural, one {# more upcoming trip} other {# more upcoming trips}}',
    description: 'Link that reveals the rest of the traveller\'s upcoming bookings on the trip planner chat landing page',
  }, { count: moreTrips.length })

  return (
    <Group direction="vertical" gap={12} fullWidth>
      <ChatListSectionLabel>
        <FormattedMessage
          id="tq1qoksuHoE21kV9zN7W"
          defaultMessage="{count, plural, one {Your upcoming trip} other {Your upcoming trips}}"
          description="Label above the list of the traveller's upcoming bookings on the trip planner chat landing page"
          values={{ count: trips.length }}
        />
      </ChatListSectionLabel>
      <Sheet border="light-foreground" borderRadius="L">
        {firstTrip && (
          <ChatLandingUpcomingTripRow
            trip={firstTrip}
            disabled={disabled}
            isBusy={submittingOrderIds.includes(firstTrip.orderId)}
            onPlanClick={onPlanClick}
          />
        )}
        {moreTrips.length > 0 && (
          <Expander expanded={showAll}>
            <div id={moreTripsId} inert={!showAll}>
              {moreTrips.map(trip => (
                <Group key={trip.orderId} direction="vertical">
                  <Divider kind="primary" />
                  <ChatLandingUpcomingTripRow
                    trip={trip}
                    disabled={disabled}
                    isBusy={submittingOrderIds.includes(trip.orderId)}
                    onPlanClick={onPlanClick}
                  />
                </Group>
              ))}
            </div>
          </Expander>
        )}
      </Sheet>
      {moreTrips.length > 0 && (
        <ChatListShowMoreToggle
          expanded={showAll}
          controlsId={moreTripsId}
          collapsedLabel={showMoreLabel}
          onClick={toggleShowAll}
        />
      )}
    </Group>
  )
}

export default ChatLandingUpcomingTrips
